<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatNumber } from '@chatwoot/utils';
import { useAlert, useTrack } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { Badge } from 'next/polaris/ui/badge';

import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxOptionMenu from './InboxOptionMenu.vue';
import InboxDisplayMenu from './InboxDisplayMenu.vue';

const props = defineProps({
  isContextMenuOpen: { type: Boolean, default: false },
  // The notifications' total (the store's meta.count); 0 or null while unknown hides the badge.
  count: { type: Number, default: null },
});

const emit = defineEmits(['redirect', 'filter']);

const { t } = useI18n();
const store = useStore();

const showInboxDisplayMenu = ref(false);
const showInboxOptionMenu = ref(false);

const hasCount = computed(() => Boolean(props.count));
const formattedCount = computed(() => formatNumber(props.count));

watch(
  () => props.isContextMenuOpen,
  val => {
    if (val) {
      showInboxDisplayMenu.value = false;
      showInboxOptionMenu.value = false;
    }
  },
  { immediate: true }
);

const markAllRead = () => {
  useTrack();
  store.dispatch('notifications/readAll').then(() => {
    useAlert(t('INBOX.ALERTS.MARK_ALL_READ'));
  });
};

const deleteAll = () => {
  store.dispatch('notifications/deleteAll').then(() => {
    useAlert(t('INBOX.ALERTS.DELETE_ALL'));
  });
};

const deleteAllRead = () => {
  store.dispatch('notifications/deleteAllRead').then(() => {
    useAlert(t('INBOX.ALERTS.DELETE_ALL_READ'));
  });
};

const openInboxDisplayMenu = () => {
  showInboxDisplayMenu.value = !showInboxDisplayMenu.value;
};

const openInboxOptionsMenu = () => {
  showInboxOptionMenu.value = !showInboxOptionMenu.value;
};

const onInboxOptionMenuClick = key => {
  const actions = {
    mark_all_read: markAllRead,
    delete_all: deleteAll,
    delete_all_read: deleteAllRead,
  };
  actions[key]?.();
  showInboxOptionMenu.value = false;
  emit('redirect');
};

const onFilterChange = option => {
  emit('filter', option);
  showInboxDisplayMenu.value = false;
  emit('redirect');
};
</script>

<template>
  <div class="flex shrink-0 flex-col gap-2 px-4 pb-3 pt-3">
    <div class="flex h-7 items-center justify-between gap-2">
      <div class="flex min-w-0 items-center gap-2">
        <h1
          class="min-w-0 truncate type-polaris-section-title text-polaris-foreground"
        >
          {{ $t('SIDEBAR.NOTIFICATIONS') }}
        </h1>
        <Badge v-if="hasCount" variant="secondary" class="shrink-0">
          {{ formattedCount }}
        </Badge>
      </div>
      <div class="relative flex shrink-0 items-center gap-1">
        <NextButton
          :aria-label="$t('INBOX.LIST.OPTIONS')"
          icon="i-lucide-sliders-vertical"
          slate
          xs
          :variant="showInboxOptionMenu ? 'faded' : 'ghost'"
          @click="openInboxOptionsMenu"
        />
        <InboxOptionMenu
          v-if="showInboxOptionMenu"
          v-on-clickaway="openInboxOptionsMenu"
          class="absolute top-full z-50 mt-1 ltr:right-0 rtl:left-0"
          @option-click="onInboxOptionMenuClick"
        />
      </div>
    </div>
    <div class="relative">
      <NextButton
        :label="$t('INBOX.LIST.DISPLAY_DROPDOWN')"
        icon="i-lucide-chevron-down"
        trailing-icon
        slate
        xs
        :variant="showInboxDisplayMenu ? 'faded' : 'solid'"
        @click="openInboxDisplayMenu"
      />
      <InboxDisplayMenu
        v-if="showInboxDisplayMenu"
        v-on-clickaway="openInboxDisplayMenu"
        class="absolute top-full mt-1 ltr:left-0 rtl:right-0"
        @filter="onFilterChange"
      />
    </div>
  </div>
</template>
